import { useMemo, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { Endpoints } from '@/api';
import { Link } from '@/components/Link/Link';
import Badge from '@/design_system/Badge';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import InputSearch from '@/design_system/InputSearch';
import PhotoCard from '@/design_system/PhotoCard';
import { RadioCard, RadioCardGroup } from '@/design_system/Radio/RadioCard/RadioCard';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconCalendar from '@/icons/Calendar.svg';
import { I18nWording, useDatabaseTranslation } from '@/services/i18n';
import { formatDate } from '@/utils/date';

type PurchaseHistoryItem =
  Endpoints['GET /clients/:clientId/purchase-history']['response']['purchases'][number];

const getPurchaseSelectionValue = (purchase: PurchaseHistoryItem) =>
  `${purchase.product.id}|${purchase.serialNumber ?? ''}|${purchase.purchaseDate}`;

export const AddItemFromPurchaseHistoryModal = ({
  isOpen,
  setIsOpen,
  onSelectProduct,
  onAddNewItem,
  purchaseHistoryData,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSelectProduct: (selection: {
    productId: string;
    purchaseDate?: string;
    serialNumber?: string;
  }) => Promise<void>;
  onAddNewItem: () => Promise<void>;
  purchaseHistoryData: Endpoints['GET /clients/:clientId/purchase-history']['response'];
}) => {
  const { t } = useLingui();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedPurchaseValue, setSelectedPurchaseValue] = useState<string | null>(null);
  const [isAddingItem, setIsAddingItem] = useState(false);

  // Client-side filtering of purchases based on search query
  const purchases = useMemo((): PurchaseHistoryItem[] => {
    const allPurchases = purchaseHistoryData?.purchases ?? [];

    if (!searchQuery.trim()) {
      return allPurchases;
    }

    const query = searchQuery.trim().toLowerCase();

    return allPurchases.filter((purchase) => {
      const productName = (purchase.product.name ?? '').toLowerCase();
      const productBrandName = (
        purchase.product.otherBrandName ??
        purchase.product.brand?.name ??
        ''
      ).toLowerCase();

      const serialNumber = (purchase.serialNumber ?? '').toLowerCase();

      return (
        productName.includes(query) ||
        productBrandName.includes(query) ||
        serialNumber.includes(query)
      );
    });
  }, [purchaseHistoryData?.purchases, searchQuery]);

  const handleAddItem = async () => {
    if (selectedPurchaseValue) {
      setIsAddingItem(true);
      const purchase = purchases.find(
        (p) => getPurchaseSelectionValue(p) === selectedPurchaseValue
      );

      if (purchase) {
        await onSelectProduct({
          productId: purchase.product.id,
          purchaseDate: purchase.purchaseDate,
          serialNumber: purchase.serialNumber,
        });
      }

      setIsOpen(false);
      setSelectedPurchaseValue(null);
      setSearchQuery('');
      setIsAddingItem(false);
    }
  };

  const handleAddNewItem = async () => {
    setIsAddingItem(true);

    await onAddNewItem();
    setIsOpen(false);
    setSelectedPurchaseValue(null);
    setSearchQuery('');
    setIsAddingItem(false);
  };

  const handleClose = () => {
    if (isAddingItem) return;
    setIsOpen(false);
    setSelectedPurchaseValue(null);
    setSearchQuery('');
  };

  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={handleClose}
      title={<Trans id="requests.new.articles.add-item-modal.title">Add an item</Trans>}
      style={{ maxWidth: '37.5rem' }}
    >
      <main>
        <Stack gap="1rem">
          <Paragraph color="secondary">
            <Trans id="requests.new.articles.add-item-modal.description">
              Please select an existing item from the client to add for service. If not listed, you
              can add a new one.
            </Trans>
          </Paragraph>

          <InputSearch
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={t({
              id: 'requests.new.articles.add-item-modal.search-placeholder',
              message: 'Search an item by name or brand...',
            })}
            ariaLabel={t({
              id: 'requests.new.articles.add-item-modal.search-aria-label',
              message: 'Search items',
            })}
            hintText={
              <Paragraph color="secondary" size="200">
                <Trans id="requests.new.articles.add-item-modal.cant-find">
                  Can&apos;t find it?{' '}
                  <Link to="#" onClick={handleAddNewItem}>
                    Add a new item
                  </Link>
                </Trans>
              </Paragraph>
            }
          />

          {purchases.length === 0 ? (
            <Paragraph color="secondary">
              <Trans id="requests.new.articles.add-item-modal.no-items">
                No items found in purchase history.
              </Trans>
            </Paragraph>
          ) : (
            <RadioCardGroup
              name="purchase-history-items"
              value={selectedPurchaseValue}
              onChange={(value) => setSelectedPurchaseValue(value)}
              direction="row"
            >
              <Stack gap="0.5rem">
                {purchases.map((purchase) => (
                  <PurchaseHistoryItemCard
                    key={getPurchaseSelectionValue(purchase)}
                    purchase={purchase}
                  />
                ))}
              </Stack>
            </RadioCardGroup>
          )}
        </Stack>
      </main>

      <Footer>
        <FooterButtons>
          <Button
            variant="primary"
            size="medium"
            onPress={handleAddItem}
            disabled={!selectedPurchaseValue}
            isLoading={isAddingItem}
          >
            <Trans id="requests.new.articles.add-item-modal.add-button">Add an item</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </Dialog>
  );
};

const PurchaseHistoryItemCard = ({ purchase }: { purchase: PurchaseHistoryItem }) => {
  const { _db } = useDatabaseTranslation();
  const purchaseDate = new Date(purchase.purchaseDate);
  const formattedDate = formatDate(purchaseDate, { dateStyle: 'medium' });
  const { product } = purchase;

  const hasSingleColor = product.colorsWithImage?.length === 1;
  const hasSingleSize = product.sizes?.length === 1;

  const singleColor: I18nWording | null =
    product.colorsWithImage?.length === 1 ? product.colorsWithImage[0].color : null;

  const singleSize: I18nWording | null =
    hasSingleSize && product.sizes && product.sizes.length > 0 ? product.sizes[0] : null;

  return (
    <RadioCard
      value={getPurchaseSelectionValue(purchase)}
      ariaLabel={product.name}
      picture={
        product.image ? (
          <PhotoCard
            url={product.image}
            name={product.name}
            alt={product.name}
            hideMoreIcon
            imgStyle={{ borderRadius: '0' }}
          />
        ) : (
          <div />
        )
      }
    >
      <Stack style={{ width: '100%' }}>
        <Stack gap="0.25rem" row justifyContent="space-between">
          <Paragraph weight="medium" ellipsis nowrap flex={1}>
            {product.name}
          </Paragraph>
          <Badge hasBorder>
            <Stack row gap="0.25rem">
              <IconCalendar style={{ fontSize: '1rem' }} />
              <InlineText size="200" color="secondary">
                {formattedDate}
              </InlineText>
            </Stack>
          </Badge>
        </Stack>
        {product.reference && (
          <InlineText size="200" color="secondary">
            <Trans id="product.reference.label">Reference:</Trans> {product.reference}
          </InlineText>
        )}
        {purchase.serialNumber && (
          <InlineText size="200" color="secondary">
            <Trans id="article.serial-number">Serial number:</Trans> {purchase.serialNumber}
          </InlineText>
        )}

        {(hasSingleColor || hasSingleSize) && (
          <Stack row gap="0.25rem">
            {hasSingleColor && singleColor && (
              <InlineText size="200" color="secondary">
                <Trans id="product.color.label">Color:</Trans> {_db(singleColor)}
              </InlineText>
            )}
            {hasSingleColor && hasSingleSize && (
              <InlineText size="200" color="secondary">
                •
              </InlineText>
            )}
            {hasSingleSize && singleSize && (
              <InlineText size="200" color="secondary">
                <Trans id="product.size.label">Size:</Trans> {_db(singleSize)}
              </InlineText>
            )}
          </Stack>
        )}
      </Stack>
    </RadioCard>
  );
};
